import { initializeApp, FirebaseApp } from 'firebase/app';
import { 
  getAuth, 
  signInWithEmailAndPassword,
  createUserWithEmailAndPassword,
  signInWithPopup,
  GoogleAuthProvider,
  signOut,
  onAuthStateChanged,
  User,
  sendPasswordResetEmail,
  sendEmailVerification,
} from 'firebase/auth';
import { getStorage } from 'firebase/storage';

const firebaseConfig = {
  apiKey: import.meta.env.VITE_FIREBASE_API_KEY,
  authDomain: import.meta.env.VITE_FIREBASE_AUTH_DOMAIN,
  projectId: import.meta.env.VITE_FIREBASE_PROJECT_ID,
  storageBucket: import.meta.env.VITE_FIREBASE_STORAGE_BUCKET,
  messagingSenderId: import.meta.env.VITE_FIREBASE_MESSAGING_SENDER_ID,
  appId: import.meta.env.VITE_FIREBASE_APP_ID,
};

// Validate Firebase configuration
const missingVars = Object.entries(firebaseConfig)
  .filter(([, value]) => !value)
  .map(([key]) => `VITE_FIREBASE_${key.replace(/([A-Z])/g, '_$1').toUpperCase()}`);

if (missingVars.length > 0) {
  const errorMessage = `Firebase configuration error: Missing environment variables: ${missingVars.join(', ')}. Please set these in your .env file or deployment environment.`;
  console.error(errorMessage);
  // Show user-friendly error in the DOM
  if (typeof document !== 'undefined') {
    document.body.innerHTML = `
      <div style="display: flex; flex-direction: column; align-items: center; justify-content: center; min-height: 100vh; background: #0A0A0A; color: #EDEDED; font-family: system-ui, -apple-system, sans-serif; padding: 20px; text-align: center;">
        <h1 style="color: #dc464a; margin-bottom: 16px;">Configuration Error</h1>
        <p style="max-width: 600px; line-height: 1.6; color: #999;">
          The application is missing required Firebase configuration. 
          This typically happens when environment variables are not set during the build process.
        </p>
        <p style="margin-top: 24px; font-size: 14px; color: #666;">
          Missing: ${missingVars.join(', ')}
        </p>
      </div>
    `;
  }
  throw new Error(errorMessage);
}

// Initialize Firebase
const app: FirebaseApp = initializeApp(firebaseConfig);
export const auth = getAuth(app);
export const storage = getStorage(app);

// Google Auth Provider
const googleProvider = new GoogleAuthProvider();
googleProvider.setCustomParameters({
  prompt: 'select_account',
});

// Auth ready state - resolves when Firebase auth state is first determined
// This is crucial for page refreshes where auth.currentUser is null until onAuthStateChanged fires
let authReadyResolve: (value: User | null) => void;
let isAuthReady = false;
export const authReady: Promise<User | null> = new Promise((resolve) => {
  authReadyResolve = resolve;
});

// Set up auth state listener to mark auth as ready
onAuthStateChanged(auth, (user) => {
  if (!isAuthReady) {
    isAuthReady = true;
    authReadyResolve(user);
  }
});

// Auth functions
export const loginWithEmail = async (email: string, password: string) => {
  const result = await signInWithEmailAndPassword(auth, email, password);
  return result.user;
};

export const registerWithEmail = async (email: string, password: string) => {
  const result = await createUserWithEmailAndPassword(auth, email, password);
  // Send email verification
  await sendEmailVerification(result.user);
  return result.user;
};

export const loginWithGoogle = async () => {
  const result = await signInWithPopup(auth, googleProvider);
  return result.user;
};

export const logout = async () => {
  await signOut(auth);
};

export const resetPassword = async (email: string) => {
  await sendPasswordResetEmail(auth, email);
};

/**
 * Get Firebase ID token for API authentication
 * 
 * IMPORTANT: This waits for Firebase auth to be ready before checking currentUser.
 * On page refresh, auth.currentUser is null until onAuthStateChanged fires.
 * Without waiting, API calls would fail with 401 because no token is sent.
 */
export const getIdToken = async (): Promise<string | null> => {
  // Wait for Firebase auth to be ready (important for page refreshes)
  await authReady;
  
  const user = auth.currentUser;
  if (!user) {
    return null;
  }
  
  return user.getIdToken();
};

export const onAuthChange = (callback: (user: User | null) => void) => {
  return onAuthStateChanged(auth, callback);
};

export const isEmailVerified = (): boolean => {
  return auth.currentUser?.emailVerified || false;
};

export const resendVerificationEmail = async (): Promise<void> => {
  const user = auth.currentUser;
  if (!user) throw new Error('No user logged in');
  await sendEmailVerification(user);
};

export const reloadUser = async (): Promise<boolean> => {
  const user = auth.currentUser;
  if (!user) return false;
  await user.reload();
  return user.emailVerified;
};

/**
 * Get the current user's authentication provider
 * Returns 'google.com' for Google sign-in, 'password' for email/password
 */
export const getAuthProvider = (): string | null => {
  const user = auth.currentUser;
  if (!user || user.providerData.length === 0) return null;
  return user.providerData[0].providerId;
};

/**
 * Check if the current user signed in with Google
 */
export const isGoogleUser = (): boolean => {
  return getAuthProvider() === 'google.com';
};

export default app;

